"use client";

import { AddToCartDialog } from "@/components/cart/add-to-cart-dialog";
import { siteConfig } from "@/lib/seo/config";
import {
	generateStockLimitDescription,
	isGenericStockLimitMessage,
	parseStockLimitMessage,
} from "@/lib/stock-message-helpers";

interface StockLimitDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	message?: string;
	selectedMeals?: string[];
	title?: string;
	detailsTitle?: string;
	secondaryActionText?: string;
}

const DEFAULT_STOCK_LIMIT_TITLE = "Selected Meals Are Temporarily Limited";
const DEFAULT_STOCK_LIMIT_MESSAGE =
	"Some selected meals are currently stock-limited due to high demand. Please choose alternative meals for now. We are restocking soon, and we appreciate your patience.";
const CONTACT_EMAIL = siteConfig.business.email;
const SUPPORT_ACTION_TEXT = `Think this may be an error or need more stock? Contact us at ${CONTACT_EMAIL}`;
const SUPPORT_ACTION_HREF = `mailto:${CONTACT_EMAIL}`;

function normalizeSelectedMeal(entry: string): string {
	const cleanedEntry = entry.trim();
	if (!cleanedEntry) return "";

	// Parse new format: "Meal (X currently in cart, only Y more left)"
	const stockLimitInfo = parseStockLimitMessage(cleanedEntry);
	if (stockLimitInfo) {
		const { mealTitle, inCartQuantity, availableQuantity } = stockLimitInfo;
		if (inCartQuantity > 0) {
			if (availableQuantity <= 0) {
				return `${mealTitle} (${inCartQuantity} currently in cart, no more left)`;
			}

			return `${mealTitle} (${inCartQuantity} currently in cart, only ${availableQuantity} more left)`;
		}

		if (availableQuantity <= 0) {
			return `${mealTitle} (out of stock)`;
		}

		return `${mealTitle} (only ${availableQuantity} left)`;
	}

	return cleanedEntry;
}

export function StockLimitDialog({
	open,
	onOpenChange,
	message,
	selectedMeals = [],
	title = DEFAULT_STOCK_LIMIT_TITLE,
	detailsTitle = "Selected meals",
	secondaryActionText = "Choose other meals",
}: StockLimitDialogProps) {
	const parsedFromMeals = selectedMeals
		.map((entry) => parseStockLimitMessage(entry))
		.filter((entry): entry is NonNullable<typeof entry> => entry !== null);

	const normalizedMeals = Array.from(
		new Set(
			selectedMeals
				.map(normalizeSelectedMeal)
				.map((meal) => meal.trim())
				.filter(Boolean),
		),
	);

	const stockLimitInfo = message ? parseStockLimitMessage(message) : null;
	const primaryParsed = stockLimitInfo ?? parsedFromMeals[0] ?? null;
	const hasCartAwareMeals =
		parsedFromMeals.some((entry) => entry.inCartQuantity > 0) ||
		(stockLimitInfo?.inCartQuantity ?? 0) > 0;
	const hasStructuredMeals = parsedFromMeals.length > 0;
	const isGenericMessage =
		typeof message !== "string" || isGenericStockLimitMessage(message);

	let finalMessage = DEFAULT_STOCK_LIMIT_MESSAGE;
	let finalTitle = title;

	if (primaryParsed && isGenericMessage) {
		if (hasStructuredMeals && parsedFromMeals.length > 1) {
			finalTitle = DEFAULT_STOCK_LIMIT_TITLE;
			finalMessage = hasCartAwareMeals
				? "Some selected meals are already in your cart, and additional quantities are limited right now. Current availability is shown below."
				: "Some selected meals are temporarily limited. Current availability is shown below. Please choose alternative meals for now.";
		} else {
			finalMessage = generateStockLimitDescription({
				mealTitle: primaryParsed.mealTitle,
				inCartQuantity: primaryParsed.inCartQuantity,
				availableQuantity: primaryParsed.availableQuantity,
			});

			if (primaryParsed.inCartQuantity > 0) {
				finalTitle = "Already In Your Cart";
			} else {
				finalTitle = "Selected Meal Is Temporarily Limited";
			}
		}
	} else if (
		message &&
		!/requested\s*\d+\s*,\s*available\s*\d+/i.test(message)
	) {
		finalMessage = message;
	}

	return (
		<AddToCartDialog
			open={open}
			onOpenChange={onOpenChange}
			variant="error"
			title={finalTitle}
			description={finalMessage}
			detailsTitle={
				normalizedMeals.length > 1 ? "Selected meals" : detailsTitle
			}
			details={normalizedMeals}
			showPrimaryAction={false}
			supportActionText={SUPPORT_ACTION_TEXT}
			supportActionHref={SUPPORT_ACTION_HREF}
			secondaryActionText={secondaryActionText}
		/>
	);
}
